import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { AppointmentStatus } from '@emr/shared';

import {
  appointmentErrorMessage,
  updateAppointmentStatus,
  type Appointment,
} from '../api/appointments.ts';
import { Button, ErrorText } from '../components/primitives.tsx';
import type { DateTimePreference } from '../format/dateTimePreference.ts';
import { useDateTimeFormat } from '../format/dateTimeFormatContext.ts';
import { formatTimeRange } from '../format/formatDateTime.ts';
import { useHasRoomForScheduleTable } from '../hooks/useMediaQuery.ts';
import { STATUS_LABELS, availableStatusActions, statusTone } from './statusActions.ts';
import { usePatient } from './usePatient.ts';

/**
 * One week-day's worth of appointments, as a desktop table or a mobile card list — never both at
 * once, matching `AccessPage`'s own responsive pattern and its reasoning against a dual DOM
 * (`claude-kit/rules/invariants.md`'s table-column strategy, and `AppShell`'s own header comment on
 * why nothing here is CSS-toggled).
 *
 * `appointmentType` and the patient's own name are both unbounded free text
 * (`MAX_APPOINTMENT_TYPE_LENGTH` = 200, `clinicalRequests.ts`'s `MAX_NAME_LENGTH` = 200), and both
 * columns are hard-wrapped (`max-w-[Nch] break-words whitespace-normal`) rather than left `nowrap` —
 * a first version of this table wrapped only the Type column and left Patient unbounded, which is
 * exactly the failure mode `claude-kit/rules/invariants.md`'s "Table columns need a deliberate
 * strategy" describes: an unconstrained cell grows the row past `min-w-[44rem]` regardless of the
 * table's own declared minimum, pushing the Actions column (Reschedule/status buttons) out of view.
 * `notes` (`MAX_APPOINTMENT_NOTES_LENGTH` = 4000) is deliberately not shown in this list at all — it
 * only appears in the reschedule dialog — so it never needed the same treatment.
 *
 * **Patient/Type caps tightened from 20ch/24ch to 16ch/18ch 2026-08-25** (`BACKLOG.md` X16). The
 * original caps left this table's overflow margin at exactly zero in the author's own measurement
 * (`table.scrollWidth` 726px against a 726px container — a pass with no slack at all), which is why
 * `pentest/tests/table-column-overflow.spec.ts`'s single-column-maxed check passed when written and
 * then failed in CI days later with no code change anywhere in this render path: none of `AppShell`,
 * `SchedulePage`, `useMediaQuery.ts`, or this file changed between the two runs (confirmed via `git
 * log`), and the same check passes cleanly on this developer's own machine and in three separately
 * reproduced Linux/Chromium environments (matching CI's OS, architecture, Playwright version, and
 * `--with-deps` font packages one at a time) — all of which measure `tableScrollWidth` pinned at
 * exactly the table's own `min-w-[44rem]` (704px) floor, never above it. Only GitHub's actual hosted
 * runner renders it wider (737px). Neither this file's CSS nor the fixture's random content explains
 * the gap (two independent CI reruns with different random content produced the identical 737px), so
 * the remaining variable is the runner's own font-substitution choice for this app's unpinned
 * `sans-serif` stack — this repo defines no `--font-sans`/`@font-face` today, so every environment
 * falls back to whatever its own fontconfig resolves generically, and that resolution genuinely
 * differs by installed-font manifest, not just OS or architecture. A `max-w-[Nch]` cap directly
 * bounds a wrapped cell's rendered width once content overflows it (true regardless of the active
 * font, since `ch` always resolves against whatever font is actually in use) — cutting each cap by
 * 4-6ch trades a small amount of wrap density for real, environment-independent margin, rather than
 * re-chasing another zero-margin pass that the next font-manifest change could tip over again. If
 * this table ever needs *reliable* pixel-level rendering parity across environments (not just "some
 * margin"), pin an explicit web font instead of tightening caps further — this fix does not do that.
 */

type StatusMutation = UseMutationResult<
  Appointment,
  unknown,
  { readonly id: string; readonly status: AppointmentStatus }
>;

/** Exported so `CalendarGrid` can tone its own appointment blocks from the same `statusTone`
 * decision (`statusActions.ts`) rather than re-deriving a second status→colour mapping. */
export function statusToneClass(status: AppointmentStatus): string {
  switch (statusTone(status)) {
    case 'accent':
      return 'text-accent-text';
    case 'success':
      return 'text-success';
    case 'danger':
      return 'text-danger';
    case 'warning':
      return 'text-warning';
    case 'muted':
      return 'text-ink-muted';
  }
}

export function AppointmentList({
  appointments,
  onReschedule,
}: {
  readonly appointments: readonly Appointment[];
  readonly onReschedule: (appointment: Appointment) => void;
}) {
  const { t } = useTranslation('schedule');
  const hasRoomForTable = useHasRoomForScheduleTable();
  const { preference } = useDateTimeFormat();
  const queryClient = useQueryClient();

  const statusChange: StatusMutation = useMutation({
    mutationFn: ({ id, status }) => updateAppointmentStatus(id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });

  if (appointments.length === 0) {
    return <p className="m-0 text-sm text-ink-muted">{t('list.noAppointments')}</p>;
  }

  return hasRoomForTable ? (
    <DesktopTable
      appointments={appointments}
      preference={preference}
      onReschedule={onReschedule}
      statusChange={statusChange}
    />
  ) : (
    <MobileCards
      appointments={appointments}
      preference={preference}
      onReschedule={onReschedule}
      statusChange={statusChange}
    />
  );
}

function PatientCell({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('schedule');
  const patient = usePatient(patientId);
  return (
    <div className="flex flex-col gap-0.5">
      {patient.data === undefined ? (
        <span className="text-ink-muted">
          {patient.isError ? t('list.unknownPatient') : t('list.loading')}
        </span>
      ) : (
        <span className="font-medium break-words text-ink">
          {patient.data.lastName}, {patient.data.firstName}
        </span>
      )}
      <Link to={`/patients/${patientId}`} className="text-sm text-accent-text hover:underline">
        {t('list.viewChart')}
      </Link>
    </div>
  );
}

function StatusActions({
  appointment,
  statusChange,
}: {
  readonly appointment: Appointment;
  readonly statusChange: StatusMutation;
}) {
  const actions = availableStatusActions(appointment.status);
  // No `?.` needed on `variables` here: once `isPending`/`isError` narrows the mutation result's own
  // discriminated union to that variant, `variables` is no longer `| undefined` on it — the
  // `MutationObserverResult` union ties "has variables" to "is not idle", not to the caller checking
  // for it.
  const isThisRowPending = statusChange.isPending && statusChange.variables.id === appointment.id;
  const thisRowFailed = statusChange.isError && statusChange.variables.id === appointment.id;

  return (
    <div className="flex flex-col items-start gap-1.5">
      <div className="flex flex-wrap gap-1.5">
        {actions.map((action) => (
          <Button
            key={action.status}
            variant="secondary"
            pending={isThisRowPending}
            onClick={() => {
              statusChange.mutate({ id: appointment.id, status: action.status });
            }}
          >
            {action.label}
          </Button>
        ))}
      </div>
      {thisRowFailed ? <ErrorText message={appointmentErrorMessage(statusChange.error)} /> : null}
    </div>
  );
}

function DesktopTable({
  appointments,
  preference,
  onReschedule,
  statusChange,
}: {
  readonly appointments: readonly Appointment[];
  readonly preference: DateTimePreference;
  readonly onReschedule: (appointment: Appointment) => void;
  readonly statusChange: StatusMutation;
}) {
  const { t } = useTranslation('schedule');
  return (
    <table className="w-full min-w-[44rem] border-collapse text-left text-sm">
      <caption className="sr-only">{t('list.caption')}</caption>
      <thead>
        <tr className="bg-table-head">
          <th scope="col" className="rounded-l-control px-3 py-2 font-semibold text-ink">
            {t('list.columns.time')}
          </th>
          <th scope="col" className="px-3 py-2 font-semibold text-ink">
            {t('list.columns.patient')}
          </th>
          <th scope="col" className="px-3 py-2 font-semibold text-ink">
            {t('list.columns.type')}
          </th>
          <th scope="col" className="px-3 py-2 font-semibold text-ink">
            {t('list.columns.status')}
          </th>
          <th scope="col" className="rounded-r-control px-3 py-2 font-semibold text-ink">
            {t('list.columns.actions')}
          </th>
        </tr>
      </thead>
      <tbody>
        {appointments.map((appointment) => (
          <tr key={appointment.id} className="border-b border-row-line last:border-b-0">
            <td className="px-3 py-3 align-top whitespace-nowrap text-ink">
              {formatTimeRange(appointment.startsAt, appointment.endsAt, preference)}
            </td>
            <td className="max-w-[16ch] px-3 py-3 align-top break-words whitespace-normal text-ink">
              <PatientCell patientId={appointment.patientId} />
            </td>
            <td className="max-w-[18ch] px-3 py-3 align-top break-words whitespace-normal text-ink">
              {appointment.appointmentType}
            </td>
            <td className="px-3 py-3 align-top whitespace-nowrap">
              <span className={`font-medium ${statusToneClass(appointment.status)}`}>
                {STATUS_LABELS[appointment.status]}
              </span>
            </td>
            <td className="px-3 py-3 align-top whitespace-normal">
              <div className="flex flex-col gap-2">
                <Button
                  variant="secondary"
                  onClick={() => {
                    onReschedule(appointment);
                  }}
                >
                  {t('list.reschedule')}
                </Button>
                <StatusActions appointment={appointment} statusChange={statusChange} />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MobileCards({
  appointments,
  preference,
  onReschedule,
  statusChange,
}: {
  readonly appointments: readonly Appointment[];
  readonly preference: DateTimePreference;
  readonly onReschedule: (appointment: Appointment) => void;
  readonly statusChange: StatusMutation;
}) {
  const { t } = useTranslation('schedule');
  return (
    <ul className="m-0 flex list-none flex-col gap-3 p-0">
      {appointments.map((appointment) => (
        <li key={appointment.id} className="rounded-control border border-line p-3 text-sm">
          <div className="flex items-baseline justify-between gap-2">
            <span className="font-medium text-ink">
              {formatTimeRange(appointment.startsAt, appointment.endsAt, preference)}
            </span>
            <span className={`font-medium ${statusToneClass(appointment.status)}`}>
              {STATUS_LABELS[appointment.status]}
            </span>
          </div>
          <div className="mt-1">
            <PatientCell patientId={appointment.patientId} />
          </div>
          <p className="m-0 mt-1 break-words text-ink">{appointment.appointmentType}</p>
          <div className="mt-2 flex flex-col gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                onReschedule(appointment);
              }}
            >
              {t('list.reschedule')}
            </Button>
            <StatusActions appointment={appointment} statusChange={statusChange} />
          </div>
        </li>
      ))}
    </ul>
  );
}
